/**
 * Bộ chọn dịch vụ trên trang đặt lịch khách — học cách form đơn của sale
 * (CEO chốt 20/08/2026), chọn lần lượt thay vì dò trong một rừng thẻ:
 *
 *   1. Nhóm sản phẩm  — 3 nút bấm (Profile / Ảnh Thẻ / Doanh Nghiệp)
 *   2. Gói sản phẩm   — ô xổ xuống; cặp "(có make)/(không make)" gộp làm MỘT
 *      ☐ Có makeup    — chỉ hiện khi gói có đủ hai biến thể
 *   3. Mục đích chụp  — do trang cha vẽ (thẻ có logo hãng bay), truyền vào đây
 *   3. Kiểu ảnh       — thẻ ảnh concept, CHỈ nhóm Profile và không bắt buộc
 *                       (một gói không bao giờ vừa có mục đích vừa có concept
 *                       nên hai khối này cùng mang số 3, như form đơn của sale)
 *
 * Dưới cùng là khối XEM TRƯỚC: ảnh minh hoạ gói lấy từ ERP, giá và cọc theo
 * đúng lựa chọn hiện tại, thời lượng, số ảnh sửa, ngày trả ảnh, quyền lợi.
 *
 * Nhóm doanh nghiệp không bán online — bấm vào ra khối liên hệ báo giá.
 */

import { useState, type ReactNode } from "react";
import { Camera, Check, Clock, Images, PackageCheck } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/ui/select";
import { formatVND } from "@/lib/format";
import {
  hasMakeupChoice,
  selectionFromProductId,
  variantOf,
  type PackageOption,
} from "@/features/products/packageOptions";
import type { PublicBookingPurpose, PublicBookingService } from "./api";
import { ConceptCards } from "./ConceptCards";
import { selectedDeposit, selectedPrice, servicePriceLabel } from "./pricing";
import { buildServiceGroups, nextPurposeId, serviceByCode, type ServiceGroup } from "./serviceGroups";

// CÂU TRẤN AN TRANG ĐIỂM — "tôi có phải tự trang điểm không".
//
// Đọc CỜ `hasMakeup` của gói (`products.has_makeup`), KHÔNG dò chữ "makeup"
// trong tên hay mô tả. Mã gói không đáng tin (pf_basic gồm trang điểm mà mã
// không nói gì), và dò chữ còn tệ hơn: một dòng ghi "không makeup" khớp y hệt
// một dòng ghi "có makeup". Bốn gói đang bán — at_basic, cv_basic_nomake,
// cv_grad_nomake, cv_pro_nomake — đã mang sẵn câu "… , không makeup." trong
// mô tả, nên lối dò chữ là hứa với khách gói không-trang-điểm rằng họ "chỉ
// cần tới", và họ chỉ biết khi đã ngồi vào ghế. Quyết định 0212.
//
// Cờ vắng = KHÔNG hứa: thà khách tới nơi thấy có sẵn còn hơn hứa có mà không
// có. Và chỉ nói ở nhóm ảnh thẻ — gói Profile khách mang trang phục riêng,
// hứa "chỉ cần tới" là hứa sai.

type Props = {
  services: PublicBookingService[];
  selected: PublicBookingService | null;
  selectedPurpose: PublicBookingPurpose | null;
  /** Gọi khi đổi gói / tick makeup: kèm mục đích còn giữ được (rỗng = phải chọn lại). */
  onSelect: (service: PublicBookingService, purposeId: string) => void;
  /** Gọi khi khách chuyển sang nhóm sản phẩm KHÁC: trang cha phải bỏ gói đang
   *  chọn. Giữ lại là khách xem tab "Profile" nhưng vẫn đặt gói ảnh thẻ cũ. */
  onClear: () => void;
  /** Khối chọn mục đích chụp của trang cha — chỉ vẽ khi gói có mục đích. */
  purposeSection: ReactNode;
  /** Mã concept đang chọn ("" = chưa chọn, hợp lệ vì concept không bắt buộc). */
  selectedConceptId: string;
  onConceptSelect: (conceptId: string) => void;
  /** Khối liên hệ báo giá, hiện khi khách chọn nhóm doanh nghiệp. */
  corporateSection: ReactNode;
};

export function ServicePicker({
  services,
  selected,
  selectedPurpose,
  onSelect,
  onClear,
  purposeSection,
  selectedConceptId,
  onConceptSelect,
  corporateSection,
}: Props) {
  const groups = buildServiceGroups(services);

  // Vị trí đang chọn suy từ mã gói trên trang cha; đổi nhóm nhưng chưa chọn
  // gói thì nhớ tạm bằng state cục bộ, y hệt ProductPicker của sale.
  const current = (() => {
    for (const group of groups) {
      const found = selectionFromProductId(group.options, selected?.code ?? "");
      if (found) return { category: group.category, ...found };
    }
    return null;
  })();
  const [pendingCategory, setPendingCategory] = useState<string | null>(null);
  const activeCategory = pendingCategory ?? current?.category ?? "";
  const activeGroup = groups.find((g) => g.category === activeCategory);
  const packageKey = pendingCategory === null ? (current?.option.key ?? "") : "";

  const pick = (option: PackageOption, wantMakeup: boolean) => {
    const variant = variantOf(option, wantMakeup);
    const service = serviceByCode(services, variant?.id);
    if (!service) return;
    onSelect(service, nextPurposeId(service, selectedPurpose?.id ?? ""));
    setPendingCategory(null);
  };

  const showMakeupTick = pendingCategory === null && current !== null && hasMakeupChoice(current.option);
  // Mục đích chụp và khối xem trước đều thuộc về GÓI ĐANG CHỌN. Đang dở dang
  // giữa hai nhóm thì giấu cả hai, nếu không khách bấm "Profile" mà bên dưới
  // vẫn hiện danh sách hãng bay của gói tiếp viên vừa xem.
  const showSelection = pendingCategory === null && selected !== null;

  return (
    <div className="space-y-4">
      <CategoryButtons
        groups={groups}
        activeCategory={activeCategory}
        onPick={(category) => {
          // Bấm lại đúng nhóm đang chọn thì quay về hiển thị gói hiện tại.
          if (current && category === current.category) {
            setPendingCategory(null);
            return;
          }
          setPendingCategory(category);
          // Sang nhóm KHÁC là bỏ gói cũ: giữ lại thì nút "Tiếp tục chọn giờ"
          // vẫn sáng và khách đặt đúng gói của nhóm mình vừa rời đi.
          onClear();
        }}
      />

      {activeGroup?.quoteOnly ? (
        corporateSection
      ) : (
        <>
          <div>
            <span className="mb-1.5 block text-sm font-medium text-muted-foreground">2. Gói sản phẩm</span>
            <Select
              value={packageKey}
              disabled={!activeGroup}
              onValueChange={(key) => {
                const option = activeGroup?.options.find((o) => o.key === key);
                // Gói tách đôi thì mặc định lấy bản CÓ makeup, như form sale.
                if (option) pick(option, true);
              }}
            >
              <SelectTrigger className="h-12 w-full" aria-label="Gói sản phẩm">
                <SelectValue placeholder={activeGroup ? "Chọn gói…" : "Chọn nhóm trước…"} />
              </SelectTrigger>
              <SelectContent>
                {(activeGroup?.options ?? []).map((option) => {
                  // Giá đi kèm tên gói cho khách khỏi phải bấm vào mới biết —
                  // giống ô mục đích của form sale. Gói CHƯA chọn thì lấy giá
                  // bản CÓ make vì bấm vào là chọn bản đó; gói ĐANG chọn thì
                  // lấy giá đúng biến thể đang tick, nếu không ô này ghi
                  // 900.000 trong khi khối xem trước ngay dưới ghi 800.000.
                  const wantMakeup = current?.option.key === option.key ? (current.withMakeup ?? true) : true;
                  const preview = serviceByCode(services, variantOf(option, wantMakeup)?.id);
                  return (
                    <SelectItem key={option.key} value={option.key}>
                      {option.label}
                      {preview ? ` · ${servicePriceLabel(preview)}` : ""}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {showMakeupTick && (
            <label className="inline-flex min-h-11 cursor-pointer items-center gap-2.5 text-base">
              <input
                type="checkbox"
                className="h-5 w-5 rounded border-input"
                checked={current.withMakeup === true}
                onChange={(e) => pick(current.option, e.target.checked)}
              />
              Có makeup
            </label>
          )}

          {showSelection && purposeSection}

          {showSelection && (
            <ConceptCards
              concepts={selected.concepts ?? []}
              selectedId={selectedConceptId}
              onSelect={onConceptSelect}
            />
          )}

          {showSelection && <ServicePreview service={selected} purpose={selectedPurpose} />}
        </>
      )}
    </div>
  );
}

/** Bước 1 — nhóm hiện thẳng thành nút bấm to (chỉ 3 nhóm, không cần xổ ra). */
function CategoryButtons({
  groups,
  activeCategory,
  onPick,
}: {
  groups: ServiceGroup[];
  activeCategory: string;
  onPick: (category: string) => void;
}) {
  return (
    <div>
      <span className="mb-1.5 block text-sm font-medium text-muted-foreground">1. Nhóm sản phẩm</span>
      <div className="flex flex-wrap gap-2" role="group" aria-label="Nhóm sản phẩm">
        {groups.map((group) => {
          const active = group.category === activeCategory;
          return (
            <button
              key={group.category}
              type="button"
              aria-pressed={active}
              onClick={() => onPick(group.category)}
              className={[
                "rounded-2xl px-5 py-3.5 text-base font-semibold leading-6 transition",
                active
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "border border-border bg-surface hover:bg-surface-2",
              ].join(" ")}
            >
              {group.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Khối xem trước gói đang chọn: ảnh, giá thật, và gói gồm những gì. */
function ServicePreview({
  service,
  purpose,
}: {
  service: PublicBookingService;
  purpose: PublicBookingPurpose | null;
}) {
  const price = selectedPrice(service, purpose);
  const deposit = selectedDeposit(service, purpose);
  const facts: { key: string; icon: ReactNode; text: string }[] = [];
  if (service.durationMin) {
    facts.push({ key: "duration", icon: <Clock className="h-3.5 w-3.5" />, text: `${service.durationMin} phút` });
  }
  if (service.editPhotos) {
    facts.push({ key: "photos", icon: <Images className="h-3.5 w-3.5" />, text: `${service.editPhotos} ảnh sửa kỹ` });
  }
  if (service.deliveryNote) {
    facts.push({
      key: "delivery",
      icon: <PackageCheck className="h-3.5 w-3.5" />,
      text: `Trả ảnh: ${service.deliveryNote}`,
    });
  }

  return (
    <div className="rounded-2xl border border-border bg-surface p-4">
      <div className="flex gap-4">
        {service.imageUrl ? (
          <img
            src={service.imageUrl}
            alt=""
            aria-hidden
            loading="lazy"
            className="h-24 w-24 shrink-0 rounded-xl object-cover sm:h-28 sm:w-28"
            // Ảnh hỏng thì giấu hẳn, đừng để khung ảnh vỡ giữa khối giá tiền.
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
        ) : (
          <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-xl bg-surface-2 text-muted-foreground sm:h-28 sm:w-28">
            <Camera className="h-6 w-6" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-muted-foreground">{service.categoryName}</p>
          <p className="mt-0.5 font-semibold">
            {service.name}
            {purpose ? ` · ${purpose.label}` : ""}
          </p>
          {service.description && (
            <p className="mt-1 text-sm text-muted-foreground">{service.description}</p>
          )}
          <p className="mt-2 text-lg font-bold text-primary">
            {price === null ? "Chọn mục đích để xem giá" : formatVND(price)}
            {deposit !== null && (
              <span className="ml-2 text-sm font-medium text-muted-foreground">
                cọc {formatVND(deposit)}
              </span>
            )}
          </p>
          {facts.length > 0 && (
            <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              {facts.map((fact) => (
                <span key={fact.key} className="inline-flex items-center gap-1">
                  {fact.icon}
                  {fact.text}
                </span>
              ))}
            </p>
          )}
        </div>
      </div>

      {service.highlights && service.highlights.length > 0 && (
        <ul className="mt-3 space-y-1 border-t border-border pt-3">
          {service.highlights.map((line) => (
            <li key={line} className="flex items-start gap-2 text-sm text-muted-foreground">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <span>{line}</span>
            </li>
          ))}
        </ul>
      )}

      {/* Câu trấn an trang điểm — lý lẽ ở khối chú thích trên đầu tệp. */}
      {service.category === "idphoto" && service.hasMakeup === true && (
        <p className="mt-3 text-sm text-muted-foreground">
          Đã gồm makeup và làm tóc — bạn chỉ cần tới.
        </p>
      )}
    </div>
  );
}
